// lib/helpers/browserDetection.ts

/**
 * Detects if the app is running in an in-app browser
 * (Facebook, Instagram, TikTok, Twitter, LinkedIn, etc.)
 */
export const isInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;

  const ua = navigator.userAgent || (window as any).opera || '';

  // Common in-app browser identifiers
  const inAppBrowsers = [
    'FBAN', // Facebook App
    'FBAV', // Facebook App
    'Instagram', // Instagram
    'FB_IAB', // Facebook In-App Browser
    'FB4A', // Facebook for Android
    'FBIOS', // Facebook for iOS
    'Twitter', // Twitter (X)
    'Line', // Line App
    'micromessenger', // WeChat
    'TikTok', // TikTok
    'Snapchat', // Snapchat
    'LinkedInApp', // LinkedIn
  ];

  return inAppBrowsers.some((browser) =>
    ua.toLowerCase().includes(browser.toLowerCase())
  );
};

/**
 * Checks if service workers are actually supported
 * (not just present in the API)
 */
export const isServiceWorkerSupported = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check basic support
  if (!('serviceWorker' in navigator)) return false;

  // Check if it's actually functional (not just a stub)
  try {
    // Some in-app browsers have the API but it doesn't work
    const isSupported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window &&
      'crypto' in window;
    return isSupported;
  } catch (e) {
    return false;
  }
};

/**
 * Checks if the environment supports FCM
 */
export const isFCMSupported = (): boolean => {
  if (typeof window === 'undefined') return false;

  return (
    !isInAppBrowser() && isServiceWorkerSupported() && window.isSecureContext
  );
};

/**
 * Gets a user-friendly message about why FCM isn't supported
 */
export const getFCMUnsupportedReason = (): string | null => {
  if (isInAppBrowser()) {
    return 'In-app browser detected. Please open in your default browser (Safari, Chrome, etc.) for notifications.';
  }

  if (!isServiceWorkerSupported()) {
    return "Your browser doesn't support push notifications.";
  }

  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return 'Secure connection (HTTPS) required for notifications.';
  }

  return null;
};

/**
 * Detects specific in-app browser type
 */
export const getInAppBrowserType = (): string | null => {
  if (typeof window === 'undefined') return null;

  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('fban') || ua.includes('fbav') || ua.includes('fb_iab')) {
    return 'Facebook';
  }
  if (ua.includes('instagram')) {
    return 'Instagram';
  }
  if (ua.includes('tiktok')) {
    return 'TikTok';
  }
  if (ua.includes('twitter')) {
    return 'Twitter';
  }
  if (ua.includes('linkedin')) {
    return 'LinkedIn';
  }
  if (ua.includes('snapchat')) {
    return 'Snapchat';
  }

  return null;
};
